/**
 * Where the patient lands after entering their code.
 *
 * It does three jobs and then gets out of the way: finish onboarding if this is
 * a first login, say what today is, and start it. There is no menu, no history,
 * no settings page, and no way to pick a session — constraint 11 puts that
 * decision on the server, and this screen only reports what it was told.
 *
 * The patient's low-stimulus preference is applied here, at the top of the
 * patient side, because it must hold across every screen including the ones
 * that come after this one.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDefinition } from '@pathways/exercises';
import { api, ApiError, type TherapyState } from '../../api.js';
import { screenChanged } from './measurement.js';
import { flushPendingResults } from './recorder.js';
import { CalibrationStep, EducationStep, GuidanceStep } from './Onboarding.js';
import {
  ACCESS_ENDED,
  DONE_FOR_TODAY,
  LOCKED_OUT_TODAY,
  NO_PROGRAM,
  PROGRAM_MISMATCH,
  SETUP_GUIDANCE,
  waitingBody,
} from './copy.js';
import styles from './TherapyHome.module.css';

type Detour = 'recalibrate' | 'guidance' | 'education' | 'mismatch' | null;

export function TherapyHome() {
  const navigate = useNavigate();
  const [detour, setDetour] = useState<Detour>(null);
  const [flagged, setFlagged] = useState(false);
  /**
   * Held locally as well as on the server so the screen changes on the tap
   * rather than after a round trip. A patient reaching for this is one the
   * current screen is already hurting.
   */
  const [lowStimulus, setLowStimulus] = useState<boolean | null>(null);
  /** Once dismissed, a changed screen is not raised again this visit. */
  const [screenPromptDismissed, setScreenPromptDismissed] = useState(false);

  const state = useQuery({
    queryKey: ['therapy', 'session'],
    queryFn: () => api.get<TherapyState>('/therapy/session'),
    retry: false,
  });

  // Anything a previous session could not deliver goes now, while the patient
  // is on a screen where a slow network costs nothing.
  useEffect(() => {
    void flushPendingResults();
  }, []);

  // The patient's own display preference (§3). Applied to the document root so
  // it holds through the runner too. Clinical canvas parameters are untouched —
  // those are the doctor's, per exercise.
  const lowStimulusOn = lowStimulus ?? state.data?.lowStimulusMode ?? false;

  useEffect(() => {
    document.documentElement.dataset.lowStimulus = lowStimulusOn ? 'true' : 'false';
    return () => {
      delete document.documentElement.dataset.lowStimulus;
    };
  }, [lowStimulusOn]);

  useEffect(() => {
    if (state.error instanceof ApiError && state.error.status === 401) {
      navigate('/therapy', { replace: true });
    }
  }, [state.error, navigate]);

  if (state.isPending) return <main className={styles.page} />;

  if (state.error instanceof ApiError && state.error.status === 403) {
    return <Message heading={ACCESS_ENDED.heading} body={ACCESS_ENDED.body} />;
  }
  if (state.error || !state.data) {
    return (
      <Message
        heading="Something went wrong"
        body="Please check your connection and refresh the page. Nothing you have done has been lost."
      />
    );
  }

  const data = state.data;

  if (flagged) {
    return <Message heading={PROGRAM_MISMATCH.heading} body={PROGRAM_MISMATCH.confirmation} />;
  }

  // --- First login, in order (§8): calibrate, set up, understand -------------
  if (!data.calibration || detour === 'recalibrate') {
    return (
      <Shell>
        <CalibrationStep
          onDone={() => {
            setDetour(null);
            setScreenPromptDismissed(true);
            void state.refetch();
          }}
          onCancel={
            detour === 'recalibrate'
              ? () => {
                  setDetour(null);
                  setScreenPromptDismissed(true);
                }
              : undefined
          }
        />
      </Shell>
    );
  }

  if (!data.educationSeen && detour === null) {
    return (
      <Shell>
        <GuidanceStep onDone={() => setDetour('education')} />
      </Shell>
    );
  }
  if (detour === 'guidance') {
    return (
      <Shell>
        <GuidanceStep onDone={() => setDetour(null)} />
      </Shell>
    );
  }
  if (!data.educationSeen || detour === 'education') {
    return (
      <Shell>
        <EducationStep
          onDone={() => {
            setDetour(null);
            void state.refetch();
          }}
        />
      </Shell>
    );
  }

  // --- The day ---------------------------------------------------------------
  const changedScreen =
    !screenPromptDismissed &&
    screenChanged(data.calibration, {
      screenWidthPx: window.screen.width,
      screenHeightPx: window.screen.height,
    });

  return (
    <Shell>
      <section className={styles.today}>
        <p className={styles.since}>
          {data.daysSinceLastSession === null
            ? 'This is your first session.'
            : data.daysSinceLastSession === 0
              ? 'Your last session was earlier today.'
              : data.daysSinceLastSession === 1
                ? 'Your last session was yesterday.'
                : `Your last session was ${data.daysSinceLastSession} days ago.`}
        </p>

        {changedScreen && (
          <div className={styles.notice}>
            <p className={styles.noticeBody}>
              This looks like a different screen from the one you set up. The
              exercises will be the wrong size until you measure it again.
            </p>
            <div className={styles.noticeActions}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setScreenPromptDismissed(true)}
              >
                Not now
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={() => setDetour('recalibrate')}
              >
                Measure this screen
              </button>
            </div>
          </div>
        )}

        {data.today.state === 'ready' ? (
          <>
            <h1 className={styles.heading}>
              {data.today.beyondAim ? 'An extra session' : 'Today'}: session{' '}
              {data.today.label}
            </h1>
            {/* Optional work is named as optional, without a count. How many
                more are allowed is never shown — that number is a target the
                moment a patient can see it (constraint 3). */}
            {data.today.beyondAim && (
              <p className={styles.optional}>
                You’ve already done what Dr. Ramirez asked for today. This one is
                extra, and only if you’re feeling good.
              </p>
            )}
            <p className={styles.lede}>
              {data.today.exercises.length === 1
                ? 'One exercise.'
                : `${data.today.exercises.length} exercises, one after another.`}{' '}
              You can stop at any point — the spacebar stops instantly.
            </p>

            <ol className={styles.plan}>
              {data.today.exercises.map((exercise) => (
                <li key={exercise.instanceId} className={styles.planItem}>
                  {exerciseName(exercise.exerciseId)}
                </li>
              ))}
            </ol>

            <button
              type="button"
              className={styles.start}
              onClick={() => navigate('/therapy/session')}
            >
              {data.today.beyondAim ? 'Start the extra session' : 'Start'}
            </button>

            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setDetour('mismatch')}
            >
              This doesn’t look like my program
            </button>
          </>
        ) : data.today.state === 'done_for_today' ? (
          <>
            <h1 className={styles.heading}>{DONE_FOR_TODAY.heading}</h1>
            <p className={styles.lede}>{DONE_FOR_TODAY.body}</p>
          </>
        ) : data.today.state === 'locked_out' ? (
          <>
            <h1 className={styles.heading}>{LOCKED_OUT_TODAY.heading}</h1>
            <p className={styles.lede}>{LOCKED_OUT_TODAY.body}</p>
          </>
        ) : data.today.state === 'waiting' ? (
          <>
            <h1 className={styles.heading}>Not just yet</h1>
            <p className={styles.lede}>
              {waitingBody(new Date(data.today.readyAt), data.today.remainingToAim)}
            </p>
          </>
        ) : (
          <>
            <h1 className={styles.heading}>{NO_PROGRAM.heading}</h1>
            <p className={styles.lede}>{NO_PROGRAM.body}</p>
          </>
        )}

        {detour === 'mismatch' && (
          <div className={styles.notice} role="dialog" aria-label={PROGRAM_MISMATCH.heading}>
            <p className={styles.noticeBody}>{PROGRAM_MISMATCH.body}</p>
            <div className={styles.noticeActions}>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setDetour(null)}
              >
                Never mind
              </button>
              <button
                type="button"
                className={styles.secondary}
                onClick={async () => {
                  try {
                    await api.post('/therapy/flag-mismatch');
                  } catch {
                    /* flagged locally either way; the message must still show */
                  }
                  setFlagged(true);
                }}
              >
                Flag this for Dr. Ramirez
              </button>
            </div>
          </div>
        )}

        <footer className={styles.footer}>
          {/* §3's low-stimulus mode. It mutes the branded chrome only — the
              exercise canvas is set by clinical parameters the doctor
              prescribes per patient, and nothing the patient taps may change
              those. Stored on the patient record so it survives a new browser
              without needing a device profile. */}
          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={lowStimulusOn}
              onChange={(e) => {
                const next = e.target.checked;
                setLowStimulus(next);
                void api
                  .patch('/therapy/preferences', { lowStimulusMode: next })
                  .catch(() => undefined);
              }}
            />
            Softer colours
          </label>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setDetour('education')}
          >
            Why stopping early matters
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setDetour('guidance')}
          >
            Setting up ({SETUP_GUIDANCE.length} things)
          </button>
          <button
            type="button"
            className={styles.linkButton}
            onClick={async () => {
              await api.post('/therapy/logout').catch(() => undefined);
              navigate('/therapy', { replace: true });
            }}
          >
            Sign out
          </button>
        </footer>
      </section>
    </Shell>
  );
}

/**
 * Exercise names come from the registry, never from a list held here — a fourth
 * exercise names itself correctly on the day it is registered (§1).
 */
function exerciseName(exerciseId: string): string {
  return getDefinition(exerciseId)?.name ?? 'Exercise';
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className={styles.page}>
      <img
        src="/brand/logo-small.png"
        alt="Pathways Brain &amp; Spinal Care"
        className={styles.logo}
        width={400}
        height={150}
      />
      {children}
    </main>
  );
}

function Message({ heading, body }: { heading: string; body: string }) {
  return (
    <Shell>
      <section className={styles.today}>
        <h1 className={styles.heading}>{heading}</h1>
        <p className={styles.lede}>{body}</p>
      </section>
    </Shell>
  );
}
